/**
 * RemoveFavouriteCommand - Remove a page from favourites
 * Follows MediatR pattern (Command + Handler in same file)
 */

import type { ICommand, ICommandHandler, IHttpClient, BaseApiResponse } from '@/application/core';
import type { FavouritesResponse, FavouriteItem } from '@/application/queries/favourites/favourites.types';
import { toCamelCase } from '@/utils/caseTransform';

// ============================================================================
// Command (Request)
// ============================================================================

export class RemoveFavouriteCommand implements ICommand<FavouritesResponse> {
  readonly type = 'RemoveFavouriteCommand' as const;

  constructor(public readonly url: string) {}
}

// ============================================================================
// Handler
// ============================================================================

export class RemoveFavouriteCommandHandler
  implements ICommandHandler<RemoveFavouriteCommand, FavouritesResponse>
{
  constructor(private readonly httpClient: IHttpClient) {}

  async handle(command: RemoveFavouriteCommand): Promise<FavouritesResponse> {
    try {
      const response = await this.httpClient.delete<BaseApiResponse<any>>(
        `/api/v1/favourites?url=${encodeURIComponent(command.url)}`
      );

      if (!response.succeeded) {
        throw new Error(response.message || 'Failed to remove favourite');
      }

      const data = response.data;

      // Transform favourite items (handle snake_case -> camelCase)
      const favourites: FavouriteItem[] = (
        data.favourites ??
        data.Favourites ??
        []
      ).map((f: any) => toCamelCase(f));

      return { favourites };
    } catch (error: any) {
      if (error.response?.status === 403 || error.status === 403) {
        throw new Error('Forbidden - insufficient permissions');
      }
      throw error;
    }
  }
}
